import { alphanum } from "components/table/lib/sort"
import { ApiResult } from "data/interface/api"
import { diff_obj } from 'data/lib/diff'
import { handleReferences } from 'data/lib/handle-references'
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"
import { push_undo } from 'data/lib/undo'
import notificationService from "data/notification"
import validationService from "data/validate"
import Client from "data/_client"
import _ from "lodash"
import { PromiseWatch } from "./lib/promise-helpers"


type SummaryAccount = {
  summaryaccountid: number
}
// private module logic //

const id: string = "summaryAccount"
const initialState: ApiResult<SummaryAccount[]> = { id, state: "init", value: [] }
const summaryAccountStream = new BehaviorSubject<ApiResult<SummaryAccount[]>>(initialState)
const summaryAccountEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let summaryAccountMap = {}
const _patches_ = PromiseWatch()

function sortData(data: SummaryAccount[]): SummaryAccount[] {
  data.sort((a, b) => alphanum(_.get(a, "accountnumber"), _.get(b, "accountnumber")));
  return data;
}

function clear(): void {
  summaryAccountStream.next({ id, state: 'ok', value: [] })
  summaryAccountMap = {}
}

function get(summaryAccountMapId: number): SummaryAccount {
  return summaryAccountMap[summaryAccountMapId]
}

function fetchSummaryAccounts(chartid:number): Promise<SummaryAccount[] | { message: string }> {
  clear()

  return Client.get(`summaryaccount?chartid=eq.${chartid}`)
  .then((records: SummaryAccount[]) => {
    summaryAccountStream.next({ id, state: 'ok', value: sortData(records) })
    validationService.fetchByParent('summaryaccount', 'chart', chartid)
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function create(chartid:number): Promise<SummaryAccount | { message: string }> {
  const streamVal = summaryAccountStream.getValue().value
  summaryAccountStream.next({ id, state: 'busy', value: streamVal })

  const payload = {
    chartid,
    status: "Active"
  }

  return Client.post(`summaryaccount`, payload)
    .then((summaryAccount: SummaryAccount) => {
      push_undo({
        undofunc: () => deleteOne(chartid, summaryAccount.summaryaccountid),
        redofunc: () => create(chartid)
      })
      summaryAccountMap[summaryAccount.summaryaccountid] = summaryAccount
      // merge results unique by summaryaccountid
      const streamVal = summaryAccountStream.getValue().value
      summaryAccountStream.next({
        id,
        state: 'ok',
        value: _.unionBy([summaryAccount], streamVal, 'summaryaccountid')
      })
      summaryAccountEventStream.next({ type: 'SUMMARYACCOUNT_CREATED', payload: summaryAccount })
      validationService.fetchByParent('summaryaccount', 'chart', chartid)
      return summaryAccount
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      summaryAccountStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      return err
    })
}

// function copy(summaryaccountid:number, chartid:number) {
//   const streamVal = summaryAccountStream.getValue().value;
//   summaryAccountStream.next({ id, state: 'busy', value: streamVal });

//   const payload = {
//     table: 'summaryaccount',
//     recordid: summaryaccountid,
//     _synchronous: true,
//     code: incrementUnique(code, "code", streamVal),
//     name: incrementUnique(name, "name", streamVal)
//   };

//   Client.post(`msg/svc_recordcopy`, payload)
//     .then((summaryAccount: SummaryAccount) => {
//       const newRecord = summaryAccount[0]
//       push_undo({
//         undofunc: () => deleteOne(newRecord.summaryaccountid),
//         redofunc: () => copy(summaryaccountid, code, name)
//       });
//       summaryAccountMap[newRecord.summaryaccountid] = newRecord
//       // merge results unique by summaryaccountid
//       const streamVal = summaryAccountStream.getValue().value
//       summaryAccountStream.next({
//         id,
//         state: 'ok',
//         value: _.unionBy([newRecord], streamVal, 'summaryaccountid')
//       })
//       summaryAccountEventStream.next({ type: 'SUMMARYACCOUNT_COPIED', payload: newRecord })
//       validationService.fetchByParent('summaryaccount', 'chart', chartid)
//       validationService.fetchAll('summaryaccount')
//       return newRecord
//     })
//     .catch((err: { error: {text: string }}) => {
//       // on error overwrite optimistic update
//       summaryAccountStream.next({ id, state: 'error', error: err, value: streamVal })
//       notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
//       return err
//     })
// }

async function deleteOne(
  chartid: number,
  summaryaccountid: number
): Promise<boolean | { message: string }> {
  const streamVal = summaryAccountStream.getValue().value

  const deleteFn = () => deleteOne(chartid, summaryaccountid)
  const refreshFn = () => fetchSummaryAccounts(chartid)
  if (!(await handleReferences('summaryaccount', summaryaccountid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted summaryaccountid
  const value = _(streamVal)
    .filter((p) => p.summaryaccountid !== summaryaccountid)
    .value()
  value.forEach((p) => (summaryAccountMap[p.summaryaccountid] = p))

  summaryAccountStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(summaryaccountid).then(() =>
  Client.del(`summaryaccount?summaryaccountid=eq.${summaryaccountid}`)
    .then((summaryAccount: SummaryAccount) => {
      delete summaryAccountMap[summaryAccount.summaryaccountid]
      push_undo({
        undofunc: () => {
          Client.post('summaryAccount', summaryAccount)
          summaryAccountMap[summaryAccount.summaryaccountid] = summaryAccount
          summaryAccountStream.next({ id, state: 'ok', value: streamVal })
        },
        redofunc: () => deleteOne(chartid, summaryaccountid)
      })
      validationService.fetchByParent('summaryaccount', 'chart', chartid)
      validationService.fetchByParent('cashflowmap', 'chart', chartid)
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      summaryAccountStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchSummaryAccounts(chartid)
      return err
    })
  )
}

async function deleteMultiple(
  chartid: number,
  summaryaccountids: number[]
): Promise<boolean | { message: string }> {
  const streamVal = summaryAccountStream.getValue().value

  const deleteFn = (accountids) => deleteMultiple(chartid, accountids)
  const refreshFn = () => fetchSummaryAccounts(chartid)
  if (!(await handleReferences('summaryaccount', summaryaccountids, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted summaryaccountids
  const value = _(streamVal)
    .filter((p) => !summaryaccountids.includes(p.summaryaccountid))
    .value()
  value.forEach((p) => (summaryAccountMap[p.summaryaccountids] = p))

  summaryAccountStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(summaryaccountids).then(() =>
  Client.del(`summaryaccount?summaryaccountid=in.${summaryaccountids}`)
    .then((summaryaccounts: SummaryAccount[]) => {
      summaryaccounts.forEach(s => delete summaryAccountMap[s.summaryaccountid])
      push_undo({
        undofunc: () => {
          Client.post('summaryaccounts', summaryaccounts)
          summaryaccounts.forEach(s => summaryAccountMap[s.summaryaccountid] = s)
          summaryAccountStream.next({ id, state: 'ok', value: streamVal })
        },
        redofunc: () => deleteMultiple(chartid, summaryaccountids)
      })
      validationService.fetchByParent('summaryaccount', 'chart', chartid)
      validationService.fetchByParent('cashflowmap', 'chart', chartid)
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      summaryAccountStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchSummaryAccounts(chartid)
      return err
    })
  )
}

function patch(summaryAccount: {
  summaryaccountid: number, chartid: number
}): Promise<SummaryAccount | { message: string }> {
  const streamVal = _.cloneDeep(summaryAccountStream.getValue().value)
  const summaryaccountid: number = summaryAccount.summaryaccountid
  const streamValsummaryAccount = _.find(streamVal, { summaryaccountid })
  const changeset = diff_obj(summaryAccount, streamValsummaryAccount)
  if (_.isEmpty(changeset)) return new Promise(() => streamVal)
  push_undo({
    undofunc: () => patch(streamValsummaryAccount),
    redofunc: () => patch(summaryAccount)
  })

  streamVal.splice(_.findIndex(streamVal, a => a.summaryaccountid === summaryaccountid), 1, summaryAccount)

  // optimistic update
  summaryAccountStream.next({
    id,
    state: 'ok',
    value: streamVal
  })

  // convert propertytype array to postgres_ style array e.g. {x,y,z}
  // if(changeset.propertytype && _.isArray(changeset.propertytype)) {
  //   changeset.propertytype = `{${_.join(changeset.propertytype,',')}}`
  // }

  return _patches_.watch(summaryaccountid, Client.patch(`summaryaccount?summaryaccountid=eq.${summaryaccountid}`, changeset)
    .then((summaryAccount: {summaryaccountid: number}) => {
      summaryAccountMap[summaryAccount.summaryaccountid] = summaryAccount
      validationService.fetch('summaryaccount', summaryaccountid)
      if ('propertytype' in changeset) validationService.fetchAll('cashflowmap')
      return summaryAccount
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      summaryAccountStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchSummaryAccounts(summaryAccount.chartid)
      return err
    }))

}

// public module interface //

const summaryAccountService = {
  stream: () => summaryAccountStream.asObservable(),
  value: () => summaryAccountStream.getValue().value,
  eventStream: () => summaryAccountEventStream.asObservable(),
  clear,
  get,
  fetch,
  fetchSummaryAccounts,
  create,
  //copy,
  deleteOne,
  deleteMultiple,
  patch
}

export default summaryAccountService
